import { useMemo, useRef, useState } from 'react'
import ModalHeader from './ModalHeader'
import useGeoFeatures from '../hooks/useGeoFeatures'
import { InputSvg, PlusSvg, RemovePointSvg } from '../Svgs'
import { checkLatLongValidation, getAoiName, toggleMinimizeModal } from '../utils/utilities'
import CustomToolTip from '../../CommonComponents/CustomToolTip'
import { InfoSvg } from '../../CommonComponents/svgs/Svgs'
import { showToastMessage } from '../../../utils/commonFunctions'
import { ActiveToolType, FeatureConfigType } from '../utils/types'
import './ConfigurationModal.scss'

type FeaturesType = 'Point' | 'Polygon'

const ConfigurationModal = () => {
    const { sidebarOpen, geoFeaturesDetails, selectedFeature, setSelectedFeature } = useGeoFeatures()
    const allFeatures = useMemo(() => geoFeaturesDetails?.allFeatures, [geoFeaturesDetails?.allFeatures])

    const open = useMemo(() => selectedFeature?.openModal === 'configuration', [selectedFeature?.openModal])
    const loading = useMemo(() => selectedFeature?.loading, [selectedFeature?.loading])

    const [configDetails, setConfigDetails] = useState<{
        type: FeaturesType;
        data: any[];
    }>({
        type: 'Point',
        data: [{ lat: '', lng: '' }],
    })
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const close = () => {
        setSelectedFeature(null)
    }

    const addPolygonRow = () => {
        setConfigDetails({ ...configDetails, data: [...configDetails.data, { lat: '', lng: '' }] });
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
            }
        }, 0);
    };

    const resetManualForm = () => {
        const data = new Array(configDetails.type === 'Point' ? 1 : 3).fill({ 'lat': '', 'lng': '' });
        setConfigDetails({
            ...configDetails,
            data: data
        })
    };

    const handleCoordChange = (index: number, field: string, value: string) => {
        if (isNaN(Number(value)) || !checkLatLongValidation(field, value)) return
        setConfigDetails({
            ...configDetails,
            data: configDetails.data.map((item, i) => {
                if (i === index) {
                    return {
                        ...item,
                        [field]: value
                    };
                }
                return item;
            })
        })
    };

    const validateManualForm = () => {
        let isValid = true;
        // Check for missing lat/lng
        configDetails.data.forEach((coord, index) => {
            if (!coord.lat || !coord.lng) {
                const label = coord.lat ? "Longitude" : "Latitude";
                const message = configDetails.type === 'Point' ? `${label} is required` : `${label} is required at row ${index + 1}`
                showToastMessage("error", message);
                isValid = false;
            }
        });

        // 🔹 Check for duplicates
        const seen = new Map<string, number>(); // store key → index
        for (let i = 0; i < configDetails.data.length; i++) {
            const coord = configDetails.data[i];
            const key = `${coord.lat},${coord.lng}`;

            if (seen.has(key)) {
                const firstIndex = seen.get(key)!;
                showToastMessage(
                    "error",
                    `Duplicate coordinates found at rows ${firstIndex + 1} and ${i + 1}`
                );
                isValid = false;
                break;
            }
            seen.set(key, i);
        }
        return isValid;
    };

    const handleManualSubmit = () => {
        if (!validateManualForm()) return;
        const coord = configDetails.data.map((coord: any) => [parseFloat(coord.lng), parseFloat(coord.lat)])
        const type = configDetails.type === 'Point' ? 'Point' : 'Polygon' as ActiveToolType
        const data = {
            type: type === 'Point' ? 'Target Track' : 'Geofence',
            description: '',
            name: getAoiName(type, allFeatures) || '',
        }
        if (type === 'Point') {
            data['coordinates'] = {
                lat: parseFloat(configDetails.data[0].lat),
                long: parseFloat(configDetails.data[0].lng),
                altitude: 0,
                minElevationAngle: 0,
            }
        } else {
            data['polygon'] = [...coord, coord[0]]
        }
        setSelectedFeature({
            ...selectedFeature!,
            data: data as FeatureConfigType,
            openModal: 'details',
            resetConfig: close
        })
        resetManualForm()
    };

    return (
        <div id='feature__configuration__modal'
            className={`feature__modal__container__wrapper feature__configuration__modal 
            ${open && 'active'} ${sidebarOpen && 'shift__left'}`}>
            <ModalHeader
                open={open ? true : false}
                title='GEOFEATURE INPUT'
                icon={<InputSvg />}
                onClose={close}
                onMinimize={() => toggleMinimizeModal('feature__configuration__modal')}
                disabled={loading ? true : false}
            />
            <div className='feature__modal__body__container'>
                <div className="feature__type__container">
                    <label className="section__label">Type</label>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                        {['Point', 'Polygon'].map((type, index) => (
                            <button
                                key={index}
                                className={`feature__type__btn ${configDetails.type === type && 'active'}`}
                                onClick={() => {
                                    if (configDetails.type === type) return
                                    const data = new Array(type === 'Point' ? 1 : 3).fill({ 'lat': '', 'lng': '' });
                                    setConfigDetails({
                                        type: type as FeaturesType,
                                        data: data
                                    })
                                }}
                            >
                                {type === 'Point' ? 'Target Track' : 'Geofence'}
                            </button>

                        ))}
                    </div>
                </div>
                {configDetails.type === 'Point' &&
                    <div className="feature__form__container">
                        {['lat', 'lng'].map((coord, index) => {
                            const label = coord === 'lat' ? 'Latitude' : 'Longitude';
                            return (
                                <div className="form__group__geofeature" key={index}>
                                    <label className="input__label">{label}</label>
                                    <div className="input__container">
                                        <input
                                            type="number"
                                            value={configDetails.data[0]?.[coord]}
                                            onChange={(e) => {
                                                handleCoordChange(0, coord, e.target.value)
                                            }}
                                        />
                                        <span className="ps-2">&deg;</span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                }

                {configDetails.type === 'Polygon' &&
                    <div className="feature__form__container polygon_section">
                        <div className="feature__form__container__header">
                            <span className="section__label">Points({configDetails.data?.length})&nbsp;
                                <CustomToolTip title='Polygon requires at least 3 points'>
                                    <span className='form__field__info'>
                                        <InfoSvg />
                                    </span>
                                </CustomToolTip>
                            </span>
                            <button
                                className="add__row__button"
                                onClick={addPolygonRow}
                                title="Add Point"
                                disabled={!configDetails.data.every(coord => coord.lat !== '' && coord.lng !== '')}
                            >
                                <PlusSvg />
                            </button>
                        </div>

                        <div className='body__container__scroll__section scroll__section' ref={scrollContainerRef}>
                            {configDetails.data?.map((coord, index) => (
                                <div key={index} className="d-flex flex-column gap-1">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span>Point {index + 1}</span>
                                        <button
                                            className="remove__row__button"
                                            onClick={() => {
                                                setConfigDetails((prev) => ({
                                                    ...prev,
                                                    data: prev.data.filter((_, i) => i !== index)
                                                }))
                                            }}
                                            disabled={configDetails.data?.length <= 3}
                                            title="Remove Point"
                                        >
                                            <RemovePointSvg length={configDetails.data?.length <= 3} />
                                        </button>
                                    </div>

                                    <div className="coord__fields__container">
                                        {['lat', 'lng'].map((type) => {
                                            const label = type === 'lat' ? 'Latitude' : 'Longitude';
                                            return (
                                                <div className="form__group__geofeature" key={type}>
                                                    <label className="input__label">{label}</label>

                                                    <div className="input__container">
                                                        <input
                                                            type="number"
                                                            value={coord[type]}
                                                            onChange={(e) => {
                                                                handleCoordChange(index, type, e.target.value)
                                                            }}
                                                        />
                                                        <span className="ps-2">&deg;</span>
                                                    </div>
                                                </div>
                                            )
                                        })}

                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                }
            </div>
            <div className="feature__modal__footer__container form__actions__container">
                <button
                    className="cancel__button"
                    onClick={() => {
                        resetManualForm();
                    }}
                >
                    Reset
                </button>
                <button
                    className="submit__button"
                    onClick={handleManualSubmit}
                >
                    Plot on Map
                </button>
            </div>
        </div>
    )
}

export default ConfigurationModal